/**
 * WashWax cart additions (Amazon style):
 *  - "Added to cart" banner at the top of the cart drawer after any add-to-cart.
 *  - "+ Add" buttons on the "Goes great with" cards (drawer and cart page).
 *
 * Works alongside the theme's cart-drawer.js / cart-items.js: it only listens to the
 * theme's own events (on:cart:add, on:line-item:change) and asks the drawer to refresh
 * with dispatch:cart-drawer:refresh. It never replaces theme behaviour.
 */
(() => {
  if (window.__wwCartLoaded) return;
  window.__wwCartLoaded = true;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const BANNER_TTL = 20000; // ms an add stays "fresh" for the banner
  let recent = null; // { variantId, at, html }

  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(cents / 100);
    } catch (e) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));

  const sized = (url, width) => {
    if (!url) return '';
    return url + (url.includes('?') ? '&' : '?') + `width=${width}`;
  };

  const drawer = () => document.getElementById('cart-drawer');
  const slot = () => document.getElementById('ww-cd-added');

  /** Builds the banner HTML for the added line item. */
  const bannerHtml = (item, cart) => {
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? `<span class="ww-cd-added__variant">${esc(item.variant_title)}</span>` : '';
    const img = item.image
      ? `<img class="ww-cd-added__img" src="${esc(sized(item.image, 120))}" alt="" width="56" height="56" loading="lazy">` : '';
    return `
      <div class="ww-cd-added__head">
        <span class="ww-cd-added__check" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 12 10 18 20 6"/></svg></span>
        <strong class="ww-cd-added__title">Added to cart</strong>
      </div>
      <div class="ww-cd-added__item">
        ${img}
        <div class="ww-cd-added__text">
          <span class="ww-cd-added__name">${esc(item.product_title)}</span>
          ${variant}
          <span class="ww-cd-added__price">${money(item.final_price, cart.currency)}</span>
        </div>
      </div>`;
  };

  /** Puts the cached banner into the drawer (the drawer re-renders its HTML on refresh). */
  const paint = () => {
    const el = slot();
    if (!el) return;
    if (recent && recent.html && Date.now() - recent.at < BANNER_TTL) {
      if (el.dataset.wwPainted !== String(recent.at)) {
        el.innerHTML = recent.html;
        el.dataset.wwPainted = String(recent.at);
      }
      el.hidden = false;
    } else {
      el.hidden = true;
      el.innerHTML = '';
      delete el.dataset.wwPainted;
    }
  };

  /** Called after any add: finds the added line in /cart.js and shows the banner. */
  const showAdded = async (variantId) => {
    recent = { variantId: variantId || null, at: Date.now(), html: '' };
    try {
      const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      if (!cart.items || !cart.items.length) return;
      // Shopify lists the most recently added line first.
      const item = (variantId && cart.items.find((i) => String(i.variant_id) === String(variantId))) || cart.items[0];
      recent.html = bannerHtml(item, cart);
      paint();
    } catch (e) {
      /* the banner is a nice-to-have; never block the cart */
    }
  };

  document.addEventListener('on:cart:add', (evt) => {
    showAdded(evt.detail && evt.detail.variantId);
  });
  document.addEventListener('on:line-item:change', (evt) => {
    const d = evt.detail || {};
    // Only an increase from a product form counts as "added", not edits inside the cart.
    if (d.newQuantity > d.oldQuantity && !(evt.target && evt.target.closest && evt.target.closest('cart-items'))) {
      showAdded(d.variantId);
    }
  });

  /** "+ Add" on the Goes great with cards. */
  document.addEventListener('click', async (evt) => {
    const btn = evt.target.closest('[data-ww-upsell-add]');
    if (!btn) return;
    evt.preventDefault();
    if (btn.disabled) return;
    const box = btn.closest('.ww-upsell');
    const err = box && box.querySelector('[data-ww-upsell-error]');
    const label = btn.textContent;
    btn.disabled = true;
    btn.classList.add('is-loading');
    if (err) err.hidden = true;
    try {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(btn.dataset.wwUpsellAdd), quantity: 1 }] })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || 'Could not add this item.');
      const variantId = btn.dataset.wwUpsellAdd;
      if (drawer() && btn.closest('#cart-drawer')) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        showAdded(variantId);
      } else {
        // Cart page: reload so totals, BOGOS gifts and discounts are all recalculated.
        window.location.reload();
      }
    } catch (e) {
      if (err) {
        err.textContent = e.message;
        err.hidden = false;
      }
      btn.disabled = false;
      btn.classList.remove('is-loading');
      btn.textContent = label;
    }
  });

  /** Keep the banner in place when the drawer re-renders, and clear it when the drawer closes. */
  const watch = () => {
    const d = drawer();
    if (!d || d.dataset.wwWatched) return;
    d.dataset.wwWatched = '1';
    new MutationObserver(() => paint()).observe(d, { childList: true, subtree: true });
    new MutationObserver(() => {
      const isOpen = d.hasAttribute('open') || d.getAttribute('aria-hidden') === 'false';
      if (!isOpen) {
        recent = null;
        paint();
      }
    }).observe(d, { attributes: true, attributeFilter: ['open', 'aria-hidden'] });
  };


  /** Cart page (phones): keep the top box and sticky bar in sync with the summary,
      and show the sticky bar only while the main checkout button is off screen. */
  const syncCartPage = () => {
    const data = document.querySelector('[data-ww-summary-data]');
    if (!data) return;
    document.querySelectorAll('[data-ww-sync-subtotal]').forEach((el) => { el.textContent = data.dataset.subtotal; });
    document.querySelectorAll('[data-ww-sync-count]').forEach((el) => { el.textContent = data.dataset.count; });
  };
  const initCartPage = () => {
    const summary = document.getElementById('cart-summary');
    const bar = document.querySelector('[data-ww-mbar]');
    if (!summary) return;
    new MutationObserver(syncCartPage).observe(summary, { childList: true, subtree: true });
    syncCartPage();
    if (!bar || !('IntersectionObserver' in window)) return;
    let mainVisible = true;
    let topVisible = true;
    const update = () => { bar.hidden = mainVisible || topVisible; };
    const watchMain = new IntersectionObserver((entries) => {
      mainVisible = entries.some((e) => e.isIntersecting);
      update();
    });
    const watchTop = new IntersectionObserver((entries) => {
      topVisible = entries.some((e) => e.isIntersecting);
      update();
    });
    const observeMain = () => {
      const btn = document.querySelector('[data-ww-main-checkout]');
      watchMain.disconnect();
      if (btn) watchMain.observe(btn);
    };
    observeMain();
    new MutationObserver(observeMain).observe(summary, { childList: true });
    const top = document.querySelector('[data-ww-mcart]');
    if (top) watchTop.observe(top); else topVisible = false;
    update();
  };

  const start = () => { watch(); initCartPage(); };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
